import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import AppServices from "../../../services/appServices";
import { APIEndpoint } from "../../../config/apiendpoint";
import { TailSpin } from "react-loader-spinner";
import { FaLightbulb } from "react-icons/fa";

interface AIInsightModalProps {
    showModal: boolean;
    handleClose: () => void;
    patientUid: string;
    patientFirstName?: string;
    patientLastName?: string;
    measurementType?: string;
}

const AIInsightModal: React.FC<AIInsightModalProps> = ({
    showModal,
    handleClose,
    patientUid,
    patientFirstName,
    patientLastName,
    measurementType,
}) => {
    const _appServices = new AppServices();
    const [loading, setLoading] = useState(true);
    const [insight, setInsight] = useState<string>("");
    const [error, setError] = useState<string>("");
    const [dataPointsSummary, setDataPointsSummary] = useState<any>(null);

    useEffect(() => {
        if (showModal && patientUid) {
            fetchAiInsight();
        }
    }, [showModal, patientUid]);

    const fetchAiInsight = async () => {
        setLoading(true);
        setError("");
        setInsight("");
        try {
            const body: any = { patientUid: patientUid };
            if (measurementType) body.measurementType = measurementType;
            const resp = await _appServices.postData(
                `${APIEndpoint.patientAiInsight}`,
                body
            );
            if (resp && resp.status === 200 && resp.success === true) {
                setInsight(resp.data.insight);
                setDataPointsSummary(resp.data.dataPointsSummary);
                _appServices.logData({
                    Severity: "Info",
                    Activity: "User generated AI insight for patient.",
                    ActivityStatus: "Success",
                    ActivityResponse: "AI Insight generated.",
                    Operation: "User Activity",
                });
            } else {
                setError(resp?.message || "Failed to generate AI insight.");
            }
        } catch (err: any) {
            console.error("Error fetching AI insight:", err);
            setError(
                err?.message || "An error occurred while generating AI insight. Please try again."
            );
            _appServices.logData({
                Severity: "Error",
                Activity: "User failed to generate AI insight for patient.",
                ActivityStatus: "Failure",
                ActivityResponse: "AI Insight generation failed.",
                Operation: "User Activity",
            });
        } finally {
            setLoading(false);
        }
    };

    const renderInsightContent = (text: string) => {
        if (!text) return null;

        const lines = text.split('\n');
        return lines.map((line, index) => {
            if (line.startsWith('### ')) {
                return <h6 key={index} className="thick-heading mt-3 mb-2">{line.replace('### ', '')}</h6>;
            }
            if (line.startsWith('## ')) {
                return <h5 key={index} className="thick-heading mt-3 mb-2">{line.replace('## ', '')}</h5>;
            }
            if (line.startsWith('- ') || line.startsWith('* ')) {
                const content = line.substring(2);
                return (
                    <div key={index} className="d-flex mb-1" style={{ paddingLeft: '10px' }}>
                        <span style={{ marginRight: '8px', color: '#03A84E' }}>&#8226;</span>
                        <span className="graphText thin-heading" dangerouslySetInnerHTML={{
                            __html: content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        }} />
                    </div>
                );
            }
            if (line.trim() === '') {
                return <br key={index} />;
            }
            return (
                <p key={index} className="graphText thin-heading mb-1" dangerouslySetInnerHTML={{
                    __html: line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                }} />
            );
        });
    };

    const buildSummaryText = () => {
        if (!dataPointsSummary) return '';
        const parts = [];
        if (dataPointsSummary.bloodPressureReadings > 0) parts.push(`${dataPointsSummary.bloodPressureReadings} blood pressure readings`);
        if (dataPointsSummary.bloodGlucoseReadings > 0) parts.push(`${dataPointsSummary.bloodGlucoseReadings} glucose readings`);
        if (dataPointsSummary.dexcomCGMDays > 0) parts.push(`${dataPointsSummary.dexcomCGMDays} CGM days`);
        if (dataPointsSummary.weightReadings > 0) parts.push(`${dataPointsSummary.weightReadings} weight measurements`);
        if (parts.length === 0) return '';
        const listText = parts.length === 1
            ? parts[0]
            : parts.slice(0, -1).join(', ') + ', and ' + parts[parts.length - 1];
        let dateRange = '';
        if (dataPointsSummary.dataStartDate && dataPointsSummary.dataEndDate) {
            const fmt = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
            dateRange = ` from ${fmt(dataPointsSummary.dataStartDate)} to ${fmt(dataPointsSummary.dataEndDate)}`;
        }
        return `Patient Insights are based on ${listText}${dateRange}.`;
    };

    return (
        <Modal
            className="modal modal-kyc send-message-popup"
            show={showModal}
            onHide={handleClose}
            animation={false}
            size="lg"
        >
            <div className="header-popup modal-header">
                <div className="d-flex align-items-center">
                    <FaLightbulb style={{ color: '#F5A623', fontSize: '22px', marginRight: '10px' }} />
                    <h6 className="thick-heading mb-0">
                        {measurementType === 'bloodPressure' ? 'Blood Pressure AI Insight' :
                         measurementType === 'bloodGlucose' ? 'Blood Glucose AI Insight' :
                         measurementType === 'weight' ? 'Weight AI Insight' :
                         'All Monitoring AI Insights'}
                        {patientLastName ? ` - ${patientLastName}, ${patientFirstName?.charAt(0)}.` : ''}
                    </h6>
                </div>
                <button onClick={handleClose} type="button" className="close">
                    <span aria-hidden="true">&times;</span>
                    <span className="sr-only">Close</span>
                </button>
            </div>
            <Modal.Body className="body-popup" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {loading ? (
                    <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '200px' }}>
                        <TailSpin
                            visible={true}
                            height="60"
                            width="60"
                            color="#03A84E"
                            ariaLabel="tail-spin-loading"
                            radius="1"
                        />
                        <p className="graphText thin-heading mt-3">Generating AI insights...</p>
                        <p className="graphText thin-heading" style={{ fontSize: '12px', color: '#999' }}>
                            This may take a few seconds
                        </p>
                    </div>
                ) : error ? (
                    <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '200px' }}>
                        <p className="text-danger">{error}</p>
                        <button
                            className="secondary-btn mt-2"
                            onClick={fetchAiInsight}
                            style={{ cursor: 'pointer' }}
                        >
                            Try Again
                        </button>
                    </div>
                ) : (
                    <div>
                        <div className="mb-3 p-3" style={{
                            backgroundColor: '#fff8e1',
                            borderRadius: '8px',
                            borderLeft: '4px solid #F5A623'
                        }}>
                            <p className="graphText" style={{ fontWeight: '600', fontSize: '13px', marginBottom: '6px' }}>
                                AI-Assisted Summary Notice
                            </p>
                            <p className="graphText thin-heading" style={{ fontSize: '12px', color: '#555', marginBottom: '0' }}>
                                This AI-generated analysis is provided as a clinical support tool only. It does not constitute medical advice, diagnosis, or treatment recommendations. Clinicians must independently verify all data, interpret findings in the context of the patient's full clinical picture, and exercise their own professional judgment before incorporating this information into the medical record or plan of care.
                            </p>
                        </div>
                        <div className="mb-3 p-3" style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            borderLeft: '4px solid #03A84E'
                        }}>
                            {renderInsightContent(insight)}
                        </div>
                        {dataPointsSummary && (
                            <div className="mt-2 pt-2" style={{ borderTop: '1px solid #eee' }}>
                                <p className="graphText thin-heading" style={{ fontSize: '11px', color: '#555', fontWeight: '600' }}>
                                    {buildSummaryText()}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default AIInsightModal;
